import React, { ChangeEvent, Component, FormEvent, ReactNode } from 'react';
import { DefaultFilter, Group, Role, User, UserType } from '../../../domain/rbac/RbacDomains';
import UserService, { UserDto, UserUpdateDto } from '../../../service/UserService';
import MessageUtil from 'util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import { isStringValid } from '../../../util/Generator';
import { CommonRoles } from 'constant/Constants';
import { query, repository } from '@trlogic/client-js';
import { InstanceContainer } from 'config/binding/InstanceBinding';
import Injectables from 'constant/Injectables';
import CollectionNames from '../../../constant/CollectionNames';
import ServiceContainer from 'config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import CustomMultiSelectBox from '../../custom/CustomMultiSelectBox';
import FormicaInput from '../../custom/FormicaInput';
import FormicaLabel from '../../custom/FormicaLabel';
import CustomPasswordInput from '../../custom/CustomPasswordInput';
import { AnimatedButton } from '@trlogic/ui-pack';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { ActiveRiskType, TransactionRiskType } from '../../../domain/stream/transaction/TransactionDomains';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import ComponentName from '../../../constant/ComponentName';

class UserEditComponent extends Component<Props, State> {
  public state = new State();

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly userService: UserService = ServiceContainer.getInstance().get(ServiceNames.USER);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render(): ReactNode {
    const { username, firstName, riskType, lastName, email, password, rePassword, roles, groups, saveLoading } = this.state;
    const { isCreate, roleItems, groupItems, checkerRequests, preview } = this.props;

    const roleSelectBoxItems = roleItems.map((role) => ({ label: role.name, value: role.name }));
    const groupSelectBoxItems = groupItems.map((group) => ({ label: group.name, value: group.name }));

    return (
      <div className={'flex flex-col w-full gap-y-4 gap-x-1'}>
        <div className={'flex flex-col gap-y-1.5'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.username', defaultMessage: 'Username' })} />
          <FormicaInput
            name={'username'}
            value={username}
            onChange={this.onChangeUsername}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            disabled={!isCreate || preview}
            label={'User Name'}
          />
        </div>
        <div className={'flex flex-col gap-y-1.5'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.firstname', defaultMessage: 'First Name' })} />
          <FormicaInput
            name={'firstName'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            value={firstName}
            disabled={preview}
            onChange={this.onChangeFirstName}
          />
        </div>
        <div className={'flex flex-col gap-y-1.5'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.lastname', defaultMessage: 'Last Name' })} />
          <FormicaInput
            name={'lastName'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            value={lastName}
            disabled={preview}
            onChange={this.onChangeLastName}
          />
        </div>
        <div className={'flex flex-col gap-y-1.5'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.email', defaultMessage: 'E-mail' })} />
          <FormicaInput
            name={'email'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            value={email}
            onChange={this.onChangeEmail}
            disabled={!isCreate || preview}
          />
        </div>
        <div className={'flex flex-col gap-y-1.5'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.password', defaultMessage: 'Password' })} />
          <CustomPasswordInput
            name={'password'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            value={password}
            onChange={this.onChangeFirstPassword}
            disabled={preview}
          />
        </div>
        <div className={'flex flex-col gap-y-1.5'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.rePassword', defaultMessage: 'Re-password' })} />
          <CustomPasswordInput
            name={'rePassword'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.default', defaultMessage: 'enter the required information' })}
            value={rePassword}
            onChange={this.onChangeSecondPassword}
            disabled={preview}
          />
        </div>

        <CustomMultiSelectBox
          options={roleSelectBoxItems}
          selectedItems={roles}
          label={LanguageUtil.get({ id: 'common.label.roles', defaultMessage: 'Roles' })}
          onChangeSelectedItems={(e: any[]) => this.setState({ roles: e })}
        />
        <CustomMultiSelectBox
          options={groupSelectBoxItems}
          selectedItems={groups}
          label={LanguageUtil.get({ id: 'common.label.groups', defaultMessage: 'Groups' })}
          onChangeSelectedItems={(e: any[]) => this.setState({ groups: e })}
          data-testid={`${ComponentName.ADMIN_USER}_groups_select`}
        />

        <div className={'flex flex-col gap-y-1.5'}>
          <FormicaLabel label={LanguageUtil.get({ id: 'common.label.type', defaultMessage: 'Type' })} />
          <FormicaSearchSelectBox
            name={'type'}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.selectType', defaultMessage: 'Select a user type.' })}
            options={this.getUserTypeOptions()}
            value={riskType}
            onChange={this.onChangeUserType}
            disabled={this.environmentUtil.isLdapOnly() || preview}
          />
        </div>

        <div className={'flex w-full items-center justify-end py-5'}>
          <AnimatedButton
            onClick={this.createOrUpdateUser}
            loading={saveLoading}
            disabled={
              this.environmentUtil.isLdapOnly() || !!checkerRequests.find((c) => c.component == ComponentName.ADMIN_USER && c.documentId == username) || preview
            }
            className={'self-end'}
            icon={'save'}
            text={LanguageUtil.get({ id: 'common.button.save', defaultMessage: 'Save' })}
            customType={'outlined'}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount(): Promise<void> {
    this.getUserData();
  }

  public async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const { selectedUser } = this.props;
    if (prevProps.selectedUser != selectedUser) {
      this.getUserData();
    }
  }

  private getUserData = async () => {
    const { selectedUser } = this.props;
    this.resetState();
    this.loadUserData();
    const groupInstances: Array<Group> = await this.repository.findMany<Group>(query.QueryBuilder.select().from(CollectionNames.GROUPS).build());
    const roleInstances: Array<Role> = await this.repository.findMany<Role>(query.QueryBuilder.select().from(CollectionNames.ROLES).build());
    if (selectedUser) {
      this.setState({});
    }
    this.setState({ groupInstances, roleInstances });
  };

  private loadUserData = () => {
    const { selectedUser, isCreate } = this.props;
    if (!isCreate && selectedUser) {
      this.setState({
        username: selectedUser.username || '',
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        role: selectedUser.role || '',
        groups: this.resolveSelectedGroups(selectedUser),
        email: selectedUser.email || '',
        passwordValidation: true,
        emailValidation: true,
        userType: selectedUser.userType,
        roles: selectedUser.roles || [],
        riskType: selectedUser.riskType || 'fraud',
        createdAt: selectedUser.createdAt,
        updatedAt: selectedUser.updatedAt,
      });
    }
  };

  private resolveSelectedGroups = (user: User): string[] => {
    const { groupItems } = this.props;
    const groupsFromUser = user.groups || [];
    const groupsFromGroupUsers = (groupItems || [])
      .filter((group) => (group.users || []).includes(user.username))
      .map((group) => group.name);
    return [...new Set([...groupsFromUser, ...groupsFromGroupUsers])];
  };

  private isUserValid = () => {
    const { username, firstName, lastName, role, email } = this.state;
    if (!(username != undefined && firstName != undefined && lastName != undefined && role != undefined && email != undefined)) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.inputFields', defaultMessage: 'Input Fields' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.warning.requiredFields', defaultMessage: 'Required fields cannot be empty.' })}</div>,
      });
      return false;
    }
    if (!(username.length > 0 && firstName.length > 0 && lastName.length > 0 && email.length > 0)) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.inputFields', defaultMessage: 'Input Fields' }),
        detail: <div>{LanguageUtil.get({ id: 'messages.warning.requiredField', defaultMessage: 'Required fields cannot be empty.' })}</div>,
      });
      return false;
    }
    return true;
  };

  private validateEmail = (value: string): boolean => {
    const regex =
      /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    return regex.test(value);
  };

  private onChangeFirstPassword = (e: FormEvent<HTMLInputElement>) => {
    const { rePassword } = this.state;
    const password: string = e.currentTarget.value;
    let passwordLengthValidation = false;
    let passwordValidation = false;
    if (password.length > 7) {
      passwordLengthValidation = true;
      if (password == rePassword) {
        passwordValidation = true;
      }
    }
    this.setState({ passwordLengthValidation, passwordValidation, password });
  };

  private onChangeUsername = (e: FormEvent<HTMLInputElement>) => {
    if (isStringValid(e.currentTarget.value)) {
      this.setState({ username: e.currentTarget.value });
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.inputFields', defaultMessage: 'Input Fields' }),
        detail: (
          <div>
            {LanguageUtil.get({
              id: 'messages.warning.inappropriate',
              defaultMessage: 'You used inappropriate characters',
            })}
          </div>
        ),
      });
    }
  };

  private createOrUpdateUser = (): void => {
    const { isCreate, checkerRequests, preview } = this.props;
    const { username } = this.state;
    if (preview) return;
    if (this.environmentUtil.isLdapOnly()) return;
    if (!!checkerRequests.find((c) => c.component == ComponentName.ADMIN_USER && c.documentId == username)) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.waitingApproval', defaultMessage: 'Waiting for approval' }),
        detail: LanguageUtil.get({
          id: 'messages.warning.waitingApproval',
          defaultMessage: "Create or Update request has been already sent to other admin users, you can't send new request",
        }),
        life: 4000,
      });
      return;
    }
    if (!this.isUserValid()) {
      return;
    }
    if (isCreate) {
      this.createUser();
    } else {
      this.updateUser();
    }
  };

  private getActiveRiskType = (riskType: TransactionRiskType): ActiveRiskType => {
    switch (riskType) {
      case 'fraud':
        return 'fraud';
      case 'compliance':
        return 'compliance';
      default:
        return 'fraud';
    }
  };

  private createUser = async () => {
    const {
      username,
      password,
      rePassword,
      userType,
      firstName,
      lastName,
      role,
      groups,
      email,
      roles,
      emailValidation,
      passwordValidation,
      riskType,
      filters,
    } = this.state;
    const {
      onHide,
      component: { module },
    } = this.props;
    if (this.environmentUtil.isLdapOnly()) return;
    const isFraud = module.includes('fraud');
    const isAml = module.includes('aml');
    const isKyc = module.includes('kyc');
    this.setState({ saveLoading: true });
    const userDto: UserDto = {
      username,
      password,
      rePassword,
      firstName,
      lastName,
      role,
      groups,
      passwordValidation,
      emailValidation,
      roles,
      email,
      userType,
      riskType: !isFraud && (isAml || isKyc) ? 'compliance' : riskType,
      activeRiskType: !isFraud && (isAml || isKyc) ? 'compliance' : this.getActiveRiskType(riskType),
      filters,
      preferences: {
        timeZone: undefined,
        twoFA: false,
        language: 'en-US',
        fontSize: '16px',
      },
    };
    try {
      const success = await this.userService.saveUser(userDto);
      if (success) {
        onHide();
        this.resetState();
      }
    } finally {
      this.setState({ saveLoading: false });
    }
  };

  private updateUser = async () => {
    const { username, riskType, password, rePassword, firstName, lastName, role, groups, email, roles, emailValidation, createdAt, updatedAt } = this.state;
    const { selectedUser } = this.props;
    const { onHide } = this.props;

    if (this.environmentUtil.isLdapOnly()) return;
    if (selectedUser == undefined) return;
    this.setState({ saveLoading: true });

    const user: User = {
      username: username!,
      firstName: firstName!,
      lastName: lastName!,
      email: email!,
      role: role,
      groups,
      userType: selectedUser.userType,
      roles,
      riskType: riskType,
      activeRiskType: this.getActiveRiskType(riskType),
      enabled: true,
      preferences: {
        timeZone: selectedUser.preferences?.timeZone || '',
        twoFA: selectedUser.preferences?.twoFA || false,
        language: selectedUser.preferences?.language || 'tr-Tr',
        fontSize: 'medium',
      },
      createdAt: createdAt,
      updatedAt: updatedAt,
    };
    const _password = password && password?.trim().length > 0 ? password : undefined;
    const _rePassword = rePassword && rePassword?.trim().length > 0 ? rePassword : undefined;
    const dto: UserUpdateDto = { user, password: _password, rePassword: _rePassword, emailValidation };
    try {
      const success = await this.userService.updateUser(dto);
      if (success) {
        MessageUtil.success({
          summary: <div>{LanguageUtil.get({ id: 'common.label.user', defaultMessage: 'User' })}</div>,
          detail: <div>{LanguageUtil.get({ id: 'common.label.updated', defaultMessage: 'updated.' })}</div>,
        });
        onHide();
        this.resetState();
      } else {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'messages.error.error', defaultMessage: 'Error' }),
          detail: <div> {LanguageUtil.get({ id: 'messages.error.savingUser', defaultMessage: 'Error when saving user!' })}</div>,
        });
      }
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.error', defaultMessage: 'Error' }),
        detail: <div> {LanguageUtil.get({ id: 'messages.error.savingUser', defaultMessage: 'Error when saving user!' })}</div>,
      });
      console.error(e);
    } finally {
      this.setState({ saveLoading: false });
    }
  };

  private onChangeSecondPassword = (e: FormEvent<HTMLInputElement>) => {
    const { password } = this.state;
    const rePassword: string = e.currentTarget.value;
    if (password != rePassword) {
      this.setState({ passwordValidation: false, rePassword });
    } else {
      this.setState({ passwordValidation: true, rePassword });
    }
  };

  private onChangeEmail = (e: FormEvent<HTMLInputElement>) => {
    const email: string = e.currentTarget.value;
    if (email.length <= 200) {
      if (this.validateEmail(e.currentTarget.value)) {
        this.setState({ emailValidation: true, email });
      } else {
        this.setState({ emailValidation: false, email });
      }
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.inputFields', defaultMessage: 'Input Fields' }),
        detail: (
          <div>{LanguageUtil.get({ id: 'messages.warning.maxEmailCharacters', defaultMessage: 'The maximum number of email characters must be 200!' })}</div>
        ),
      });
    }
  };

  private onChangeFirstName = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ firstName: e.currentTarget.value });
  };

  private onChangeLastName = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ lastName: e.currentTarget.value });
  };

  private getUserTypeOptions() {
    const {
      component: { module },
    } = this.props;
    const options = [];
    const isAml = module.includes('aml');
    const isFraud = module.includes('fraud');
    const isKyc = module.includes('kyc');
    if (isAml || isKyc) {
      options.push({ label: LanguageUtil.get({ id: 'common.label.compliance', defaultMessage: 'Compliance' }), value: 'compliance' });
    }
    if (isFraud) {
      options.push({ label: LanguageUtil.get({ id: 'common.label.fraud', defaultMessage: 'Fraud' }), value: 'fraud' });
    }
    if (isAml && isFraud) {
      options.push({ label: LanguageUtil.get({ id: 'common.label.all', defaultMessage: 'All' }), value: 'all' });
    }
    return options;
  }

  private onChangeUserType = (e: FormicaSelectBoxItem) => {
    this.setState({ riskType: e.value as User['riskType'] });
  };

  private resetState = (): void => {
    this.setState({
      username: '',
      firstName: '',
      lastName: '',
      password: '',
      rePassword: '',
      role: 'Member',
      riskType: 'fraud',
      groups: [],
      roles: [],
      filters: [],
      passwordValidation: true,
      emailValidation: true,
      passwordLengthValidation: true,
      saveLoading: false,
      groupInstances: [],
      roleInstances: [],
      email: '',
      userType: 1,
      createdAt: undefined,
      updatedAt: undefined,
    });
  };
}

const mapStateToProps = (state: RootState) => ({
  component: state.component,
});
const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

interface Props extends ReduxProps {
  selectedUser?: User;
  setSelectedUser: (user: User) => void;
  roleItems: Role[];
  groupItems: Group[];
  isCreate: boolean;
  onHide: () => void;
  checkerRequests: CheckerRequest[];
  preview?: boolean;
}

class State {
  username: string | undefined;
  password: string | undefined;
  rePassword: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  role: CommonRoles = 'Member';
  groups: Array<string> = new Array<string>();
  passwordValidation: boolean = true;
  emailValidation: boolean = true;
  roles: Array<string> = new Array<string>();
  email: string | undefined;
  userType: UserType = 1;
  passwordLengthValidation: boolean = true;
  activeRiskType: ActiveRiskType = 'fraud';
  riskType: TransactionRiskType = 'fraud';
  filters: DefaultFilter[] = [];
  groupInstances: Array<Group> = [];
  roleInstances: Array<Role> = [];
  saveLoading: boolean = false;
  createdAt: number | undefined;
  updatedAt: number | undefined;
}

export default connector(UserEditComponent);
